import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import cns from 'classnames';
import { Link } from 'react-router-dom';
import { getFileWithSignedUrl } from '../../../shared/actions/Files';
import { IMAGE_PROPS, overlayImageIds } from '../../../shared/utils/ImageUtils';
import UserCollectionCardDisplay from './UserCollectionCardDisplay';
import { DEFAULT_IMAGE_URLS } from '../../../shared/Constants';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../shared/components/VerbButton';
import VerbDropdownButton from '../../../shared/components/VerbDropdownButton';
import { RuntimeContext } from '../../../../common/RuntimeContext';

const buttonTextDict = {
    STARTED: {
        message: 'CONTINUE',
        textId: 'BUTTON_CONTINUE'
    },
    ASSIGNED: {
        message: 'BEGIN',
        textId: 'BUTTON_BEGIN'
    },
    IN_PROGRESS: {
        message: 'CONTINUE',
        textId: 'BUTTON_CONTINUE'
    },
    COMPLETED: {
        message: 'REVIEW',
        textId: 'BUTTON_REVIEW'
    }
};

const CollectionSliderCard = (props) => {
    const config = useContext(RuntimeContext)
    const {
        collection,
        collection: {
            _id,
            collectionId,
            name,
            thumbnailImageUrl,
            objective = '',
            completedLearningAssets,
            totalLearningAssets,
            isCustomCollection = false,
            collectionSubtitle,
            collectionName,
            subtitle,
            status,
            isAssigned,
            isRecommendedByTeamLead,
            type,
            collectionPermalink = '',
            permalink = ''
        },
        isUserCollection,
        cardOrientation = 'vertical',
        className = 'col-12 col-sm-6',
        isReadOnly = false,
        segmentEventHandler,
        handleRecommendationModalOpen,
        loading,
        isTeams = false,
        hideDetailsOverlay = false,
        overlayMode = '',
        cardFooter,
        cardTextProp,
        cardOverlayProps,
        hideAdditionalOptions = false,
        displayCountOnOverlay = true
    } = props;

    const link = `/collection/${permalink || collectionPermalink || collectionId || _id}`;

    if (loading) {
        return (
            <div
                key={collectionId}
                className={cns(cardOrientation === 'vertical' ? 'vertical-collection-card' : 'user-collection-card')}
            >
                <div style={{ width: '100%', paddingTop: '100%' }} className={'col-12 collection-display loading'} />
                <div style={{ minHeight: '35px', marginTop: '10px' }} className={'col-12 loading'} />
            </div>
        );
    }

    const _getOverlayHeight = (showProgressBar) => {
        const { thumbnailDimension = { w: 350, h: 350 } } = props;
        return (
            thumbnailDimension.w * 0.18 +
            (showProgressBar ? Math.ceil(((thumbnailDimension.w - 250) / thumbnailDimension.w + 1) * 8) : 0)
        ).toString();
    };

    const _getOverlayTextY = (showProgressBar) => {
        const { thumbnailDimension = { w: 350, h: 350 } } = props;
        return (
            thumbnailDimension.w * 0.06 +
            (showProgressBar ? Math.ceil(((thumbnailDimension.w - 250) / thumbnailDimension.w + 1) * 8) : 0)
        ).toString();
    };

    const _getCollectionImageProps = (completionPercentage) => {
        const {
            intl: { formatMessage },
            collection,
            thumbnailDimension = { w: 350, h: 350 } // the actual size of the fetched image. This should ideally be larger than the card
        } = props;
        const showProgressBar =
            isUserCollection && completionPercentage !== 100 && collection?.content?.some((asset) => asset.isCompleted);
        const cloudinaryImageProps = IMAGE_PROPS.collectionImageCloudinary;
        const imageSource = thumbnailImageUrl || DEFAULT_IMAGE_URLS.LEARNING_ASSET_THUMBNAIL_IMAGE;
        let overlay = {
            opacity: '60',
            gravity: 'north',
            dimension: {
                h: _getOverlayHeight(showProgressBar),
                w: thumbnailDimension.w.toString()
            }
        };

        let textProp = {};

        if (!isReadOnly) {
            textProp = {
                fontFamily: 'Avenir-Bold.ttf',
                fontWeight: (thumbnailDimension.w * 0.072).toString(),
                alignment: 'center',
                textY: _getOverlayTextY(showProgressBar),
                textColor: 'FFFFFF',
                backgroundWidth: thumbnailDimension.w.toString(),
                gravity: 'north'
            };
            if (status === 'COMPLETED' || completionPercentage === 100) {
                textProp.message = formatMessage({ id: 'HEADING_COMPLETE', defaultMessage: 'COMPLETE' }, {});
                textProp.backgroundHeight = thumbnailDimension.h.toString();
                overlay.overlayId = 'teal';
            } else if (cardOverlayProps && cardTextProp && !(status === 'COMPLETED' || completionPercentage === 100)) {
                overlay = !isEmpty(cardOverlayProps) ? { ...overlay, ...cardOverlayProps } : {};
                textProp = !isEmpty(cardTextProp)
                    ? { ...textProp, ...cardTextProp, backgroundHeight: thumbnailDimension.h.toString() }
                    : {};
            } else if (
                !isRecommendedByTeamLead && // do not show assigned banner if recommended/assigned by team
                (isAssigned ||
                    status === 'ASSIGNED' ||
                    (collection.recommendationInfo && collection.recommendationInfo.code === 'ASSIGNED'))
            ) {
                textProp.message = formatMessage({ id: 'HEADING_ASSIGNED', defaultMessage: 'ASSIGNED' }, {});
                textProp.backgroundHeight = thumbnailDimension.h.toString();
                overlay.overlayId = 'pink';
            } else {
                overlay = {};
                textProp = {};
            }
        }

        if (collection.isLocked) {
            overlay = {
                overlayId: overlayImageIds.locked,
                dimension: {
                    h: thumbnailDimension.h,
                    w: thumbnailDimension.w
                }
            };
        }

        return {
            imageSource,
            dimension: { ...thumbnailDimension },
            cloudinaryCrop: cloudinaryImageProps.cloudinaryCrop,
            textProp,
            overlay,
            showProgressBar,
            completionPercentage
        };
    };

    const getSubtitleSection = (objective, collectionSubtitle, isCustomCollection) => {
        if (isCustomCollection && !objective) {
            return `This is your default collection. Here you'll find all of the courses, activities, articles, and videos that you are currently working on.`;
        }
        if (objective) {
            return objective;
        }

        return collectionSubtitle || '';
    };

    const _completePercent = () => {
        let completePercent = 0;
        if (collection.content) {
            completePercent = Math.round(((collection.content.filter((asset) => asset.isCompleted).length)/collection?.content?.length ?? 0) * 100)
        }
        return completePercent
    };

    const _makeFooter = () => {
        let primaryDetail = null;
        let secondaryDetail = null;
        let thirdDetail = null;

        if (collection.content || collection.contentIds) {
            const collectionCount = collection.content ? collection.content.length : collection.contentIds.length;
            primaryDetail = (
                <span className={'content-count verb-title-6'}>
                    <i className={'fad fa-book meta-icon-size meta-vertical-align'} />
                    <span className={'meta-vertical-align meta-space'}>{collectionCount}</span>
                </span>
            );
        }
        if ((collection.durationInMinutes || collection.totalTimeForCollection) && !collection.memberCount) {
            const collectionTime = collection.durationInMinutes || collection.totalTimeForCollection;
            const collectionText = collectionTime < 60 ? `${collectionTime}m` : `${Math.round(collectionTime / 60)}h`;
            secondaryDetail = (
                <span className={'content-time verb-title-6'}>
                    <i className={'fad fa-clock meta-icon-size meta-vertical-align'} />
                    <span className={'meta-vertical-align meta-space'}>{collectionText}</span>
                </span>
            );
        }

        if (collection.memberCount) {
            secondaryDetail = (
                <span className={'content-time verb-title-6 '}>
                    <i className={'fad fa-users meta-icon-size meta-vertical-align'} />
                    <span className={'meta-vertical-align meta-space'}>{collection.memberCount}</span>
                </span>
            );
        }

        if (collection.recommendationCount) {
            thirdDetail = (
                <span className={secondaryDetail ? 'margin-left verb-title-6' : 'margin-right verb-title-6'}>
                    <i className={'fad fa-paper-plane meta-icon-size meta-vertical-align'} />
                    <span className={'meta-vertical-align meta-space'}>{collection.recommendationCount}</span>
                </span>
            );
        }

        return (
            <div className={'asset-metadata-container col-12'}>
                <div className={'row'}>
                    <div className={'col-12'}>
                        {displayCountOnOverlay && primaryDetail}
                        {cardOrientation !== 'vertical' && <span>&nbsp;&nbsp;&nbsp;</span>}
                        {displayCountOnOverlay && secondaryDetail}
                        {thirdDetail}
                    </div>
                </div>
            </div>
        );
    };

    const _makeButton = () => {
        if (isReadOnly) {
            return null;
        }

        let collectionBtnStatus = collection.status;
        
        if(collection.percentComplete === 0){
            collectionBtnStatus = "ASSIGNED";
        }
        if(collection.percentComplete > 0 || collection.percentComplete < 100){
            collectionBtnStatus = "IN_PROGRESS";
        }
        if(collection.percentComplete === 100 || collection.status === 'COMPLETED'){
            collectionBtnStatus = "COMPLETED";
        }
        
        const buttonText = buttonTextDict[collectionBtnStatus] || buttonTextDict.ASSIGNED;
        const dropdownData = [
            {
                text: 'Go to Path',
                fontAwesomeIcon: 'fa-layer-group',
                fontAwesomeIconWeight: 'fal',
                linkProps: {
                    to: {
                        pathname: `/collection/${
                            collection.permalink ||
                            collection.collectionPermalink ||
                            collection.collectionId ||
                            collection._id
                        }/path`
                    }
                }
            },
            // {
            //     text: 'Recommend Collection',
            //     fontAwesomeIcon: 'fa-paper-plane',
            //     fontAwesomeIconWeight: 'fal',
            //     onClick: handleRecommendationModalOpen
            // }
        ];

        if (isTeams) {
            dropdownData.splice(1, 1);
        }

        if (collection.status && collection.status === 'COMPLETED' && (collection.resources || []).length) {
            dropdownData.push({
                text: 'Reference Files',
                fontAwesomeIcon: 'fa-download',
                fontAwesomeIconWeight: 'fal',
                onClick: () => {
                    (collection.resources || []).forEach((item) => getFileWithSignedUrl(config.pathsWeb, item.url.split(' ').join('+')));
                }
            });
        }

        return (
            <div className={'button-row row no-horizontal-margin no-padding'}>
                <div className={`col-md-9 col-8 no-padding`}>
                    <Link className={'card-link'} to={link}>
                        <VerbButton
                            className={'cta-button'}
                            textId={buttonText.textId}
                            defaultMessage={buttonText.message}
                        />
                    </Link>
                </div>
                {hideAdditionalOptions ? null : (
                    <div className={'col-md-3 col-4 no-padding vertical-cards-popover-button-container'}>
                        <VerbDropdownButton
                            fontAwesomeIcon={'fa-ellipsis-h'}
                            dropdownData={dropdownData}
                            className={'vertical-cards-popover-button'}
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                            popoverPlacement={'top'}
                        />
                    </div>
                )}
            </div>
        );
    };

    const _getCardHeadingProps = () => {
        if (collection.type === 'STANDARD') {
            return { text: 'CERTIFIED PATH', headingClass: 'certified' };
        }
        return { text: 'PATH', headingClass: '' };
    };

    let completePercent = 0;

    if (isUserCollection) {
        completePercent = _completePercent();
    }

    // Props to pass to UserCollectionCardDisplay
    const assetTitleToRender = name || collectionName;
    const cardHeadingProps = _getCardHeadingProps();
    const cardSubtitle = getSubtitleSection(objective, subtitle || collectionSubtitle, isCustomCollection);
    const footer = _makeFooter();
    const button = cardOrientation === 'vertical' ? _makeButton() : null;
    const imageProps = _getCollectionImageProps(completePercent);
    return (
        <UserCollectionCardDisplay
            loading={loading}
            collectionId={_id}
            isReadOnly={isReadOnly}
            subtitle={cardSubtitle}
            title={assetTitleToRender}
            link={link}
            cardHeadingProps={cardHeadingProps}
            footer={cardFooter || footer}
            className={className}
            segmentEventHandler={segmentEventHandler}
            imageProps={imageProps}
            cardOrientation={cardOrientation}
            actionButton={!hideDetailsOverlay ? button : null}
            hideDetailsOverlay={hideDetailsOverlay}
            overlayMode={overlayMode}
        />
    );
};

CollectionSliderCard.propTypes = {
    cardOrientation: PropTypes.oneOf(['vertical', 'horizontal']),
    collection: PropTypes.shape({
        _id: PropTypes.string,
        name: PropTypes.string,
        thumbnailImageUrl: PropTypes.string,
        subtitle: PropTypes.string,
        description: PropTypes.string,
        objective: PropTypes.string,
        percentComplete: PropTypes.number,
        completedLearningAssets: PropTypes.number,
        totalLearningAssets: PropTypes.number,
        timeSpentOnCollection: PropTypes.number,
        totalTimeForCollection: PropTypes.number,
        durationInMinutes: PropTypes.number,
        isCustomCollection: PropTypes.bool,
        isLocked: PropTypes.bool,
        hasCompletedData: PropTypes.bool,
        collectionId: PropTypes.string,
        collectionSubtitle: PropTypes.string,
        collectionName: PropTypes.string,
        isAssigned: PropTypes.bool,
        isRecommendedByTeamLead: PropTypes.bool,
        status: PropTypes.string,
        type: PropTypes.string,
        content: PropTypes.array,
        contentIds: PropTypes.array,
        recommendationInfo: PropTypes.object,
        memberCount: PropTypes.number,
        recommendationCount: PropTypes.number,
        resources: PropTypes.array,
        permalink: PropTypes.string,
        collectionPermalink: PropTypes.string
    }),
    isReadOnly: PropTypes.bool,
    isUserCollection: PropTypes.bool,
    handleRecommendationModalOpen: PropTypes.func,
    // eslint-disable-next-line react/no-unused-prop-types
    intl: PropTypes.object,
    className: PropTypes.string,
    segmentEventHandler: PropTypes.func,
    thumbnailDimension: PropTypes.object,
    loading: PropTypes.bool,
    isTeams: PropTypes.bool,
    hideDetailsOverlay: PropTypes.bool,
    hideAdditionalOptions: PropTypes.bool,
    overlayMode: PropTypes.string,
    cardFooter: PropTypes.string,
    cardTextProp: PropTypes.string,
    cardOverlayProps: PropTypes.string
};

export const CollectionSliderCardComponent = CollectionSliderCard;

export default injectIntl(CollectionSliderCard);
